import { useEffect, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import {
  KIND_LABELS,
  LIFECYCLE_LABELS,
  leadStatusesApi,
  type LeadLifecycleStatus,
  type LeadStatusDefinition,
  type LeadStatusKind,
  type LeadStatusList,
} from '../api/leadStatuses'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  useConfirm,
  FormSection,
} from '../components/ui'

/**
 * F-102 (TASK-0356, REQ-068-001/002): stati configurabili del lead. Sopra il ciclo di vita fisso (Nuovo, Contattato,
 * Qualificato, Convertito, Perso) l'Admin definisce stati operativi (il lead resta aperto) ed esiti finali (il lead si
 * chiude: Perso con il suo motivo, o Convertito). Lettura per tutti, scrittura solo Admin (il server risponde 403 agli
 * altri: qui si nascondono solo i pulsanti). Nessuno stato e' seminato: "Applica struttura standard" e' un'azione
 * esplicita e si puo' ripetere senza duplicare nulla.
 */
interface FormState {
  id?: number
  code: string
  label: string
  kind: LeadStatusKind
  lifecycleStatus: LeadLifecycleStatus
  lostReason: string
  sortOrder: number
  isActive: boolean
  leads: number
}

const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  kind: 'operativo',
  lifecycleStatus: 'contacted',
  lostReason: '',
  sortOrder: 0,
  isActive: true,
  leads: 0,
}

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

/** Cicli di vita ammessi per tipo (stessa regola del server). */
function lifecycleOptions(kind: LeadStatusKind): LeadLifecycleStatus[] {
  return kind === 'esito_finale' ? ['lost', 'converted'] : ['new', 'contacted', 'qualified']
}

export function LeadStatusesPage({ auth }: { auth: LoginResponse }) {
  const askConfirm = useConfirm()
  const canWrite = isAdminRole(auth)
  const [data, setData] = useState<LeadStatusList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [showStandard, setShowStandard] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setData(await leadStatusesApi.list())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(d: LeadStatusDefinition) {
    setForm({
      id: d.id,
      code: d.code,
      label: d.label,
      kind: d.kind,
      lifecycleStatus: d.lifecycleStatus,
      lostReason: d.lostReason ?? '',
      sortOrder: d.sortOrder,
      isActive: d.isActive,
      leads: d.leads,
    })
    setModalError(null)
    setShowModal(true)
  }

  function closeModal() {
    if (saving) return
    setShowModal(false)
    setModalError(null)
  }

  function changeKind(kind: LeadStatusKind) {
    setForm(f => ({
      ...f,
      kind,
      lifecycleStatus: lifecycleOptions(kind).includes(f.lifecycleStatus) ? f.lifecycleStatus : lifecycleOptions(kind)[0]!,
      lostReason: kind === 'esito_finale' ? f.lostReason : '',
    }))
  }

  const needsReason = form.kind === 'esito_finale' && form.lifecycleStatus === 'lost'

  async function handleSave() {
    setSaving(true)
    setModalError(null)
    try {
      const body = {
        label: form.label.trim(),
        kind: form.kind,
        lifecycleStatus: form.lifecycleStatus,
        lostReason: needsReason ? form.lostReason : null,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      }
      if (form.id) await leadStatusesApi.update(form.id, body)
      else await leadStatusesApi.create({ ...body, code: form.code.trim() })
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(d: LeadStatusDefinition) {
    if (!(await askConfirm({ title: 'Elimina stato', message: `Eliminare lo stato «${d.label}»?`, confirmLabel: 'Elimina', destructive: true }))) return
    setError(null)
    setNotice(null)
    try {
      await leadStatusesApi.delete(d.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore nell'eliminazione")
    }
  }

  async function handleApplyStandard() {
    const toInsert = (data?.standard ?? []).filter(s => s.kind !== 'derivato').length
    if (
      !(await askConfirm({ title: 'Applica struttura standard', message: `Applicare la struttura standard? Vengono inseriti gli stati che mancano (fino a ${toInsert}); ` +
          'quelli già presenti non cambiano. Gli stati calcolati dal sistema (tentativi, appuntamenti, no show) restano calcolati.', confirmLabel: 'Applica' }))
    ) return
    setError(null)
    setNotice(null)
    try {
      const r = await leadStatusesApi.applyStandard()
      setNotice(
        r.inserted === 0
          ? 'La struttura standard era già presente: nessuno stato inserito.'
          : `Inseriti ${r.inserted} stati (già presenti: ${r.alreadyPresent}).`,
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore nell'applicazione")
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  const items = data?.items ?? []

  return (
    <Page>
      <PageHeader
        title="Stati dei lead"
        icon="label"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          canWrite ? (
            <>
              <Button variant="outlined" onClick={() => void handleApplyStandard()}>
                Applica struttura standard
              </Button>
              <Button variant="filled" icon="add" onClick={openCreate}>
                Nuovo stato
              </Button>
            </>
          ) : undefined
        }
      />

      <Callout tone="info">Stati operativi ed esiti finali sopra il ciclo di vita del lead (Nuovo, Contattato, Qualificato, Convertito, Perso), che non cambia. Lo stato operativo tiene il lead aperto; l'esito finale lo chiude.</Callout>

      {error && <Callout tone="error">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      <Card variant="outlined" padding="none">
        {items.length === 0 ? (
          <EmptyState
            icon="label"
            title="Nessuno stato configurato"
            description={`I lead mostrano solo il ciclo di vita e lo stato calcolato.${canWrite ? ' Puoi applicare la struttura standard o crearne uno.' : ''}`}
          />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Ordine</th>
                <th>Stato</th>
                <th>Tipo</th>
                <th>Ciclo di vita</th>
                <th>Motivo di perdita</th>
                <th>Lead</th>
                <th>Stato</th>
                {canWrite && <th>Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(d => (
                <tr key={d.id}>
                  <td className="md-num">{d.sortOrder}</td>
                  <td>
                    <span className="md-typescale-emphasized-body-medium">{d.label}</span>
                    <br />
                    <small className="text-muted">{d.code}{d.isStandard ? ' · standard' : ''}</small>
                  </td>
                  <td>
                    <StatusPill tone={d.kind === 'esito_finale' ? 'neutral' : 'info'}>
                      {KIND_LABELS[d.kind]}
                    </StatusPill>
                  </td>
                  <td>{LIFECYCLE_LABELS[d.lifecycleStatus]}</td>
                  <td>{d.lostReason ?? <span className="text-muted">—</span>}</td>
                  <td className="md-num">{d.leads}</td>
                  <td>
                    <StatusPill tone={d.isActive ? 'success' : 'neutral'}>
                      {d.isActive ? 'Attivo' : 'Disattivo'}
                    </StatusPill>
                  </td>
                  {canWrite && (
                    <td>
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(d)} />{' '}
                      <IconButton tooltip={d.leads > 0 ? 'Usato da dei lead: disattivalo invece di eliminarlo' : 'Elimina'}
                        icon="delete"
                        size="sm"
                        disabled={d.leads > 0}
                        aria-label="Elimina"
                        onClick={() => void handleDelete(d)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <div>
        <Button variant="text" size="sm" onClick={() => setShowStandard(s => !s)}>
          {showStandard ? 'Nascondi' : 'Mostra'} la struttura standard della specifica (24 stati)
        </Button>
      </div>
      {showStandard && (
        <Card variant="outlined" padding="none">
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Stato</th>
                <th>Tipo</th>
                <th>Ciclo di vita</th>
                <th>Motivo di perdita</th>
              </tr>
            </thead>
            <tbody>
              {(data?.standard ?? []).map(s => (
                <tr key={s.code}>
                  <td>{s.label}</td>
                  <td>{KIND_LABELS[s.kind]}</td>
                  <td>{s.lifecycleStatus ? LIFECYCLE_LABELS[s.lifecycleStatus] : <span className="text-muted">calcolato</span>}</td>
                  <td>{s.lostReason ?? <span className="text-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Card>
      )}

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={form.id ? 'Modifica stato' : 'Nuovo stato'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              disabled={saving || !form.label.trim() || (!form.id && !form.code.trim()) || (needsReason && !form.lostReason)}
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}

          <FormSection title="Stato">
            <TextField
              className="md-span-full"
              id="ls-code"
              label="Codice *"
              type="text"
              value={form.code}
              supportingText="es. richiamo_serale. Minuscolo, con lettere, cifre e underscore. Non si cambia più dopo la creazione."
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              disabled={saving || form.id !== undefined}
            />
            <TextField
              id="ls-label"
              label="Etichetta *"
              type="text"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              disabled={saving}
            />
            <TextField
              id="ls-order"
              label="Ordine"
              type="number"
              value={form.sortOrder}
              onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
              disabled={saving}
            />
          </FormSection>

          <FormSection title="Classificazione">
            <Select
              id="ls-kind"
              label="Tipo"
              value={form.kind}
              onChange={e => changeKind(e.target.value as LeadStatusKind)}
              disabled={saving || form.leads > 0}
            >
              <option value="operativo">{KIND_LABELS.operativo} (il lead resta aperto)</option>
              <option value="esito_finale">{KIND_LABELS.esito_finale} (chiude il lead)</option>
            </Select>
            <Select
              id="ls-life"
              label="Ciclo di vita"
              value={form.lifecycleStatus}
              onChange={e => setForm(f => ({ ...f, lifecycleStatus: e.target.value as LeadLifecycleStatus }))}
              disabled={saving || form.leads > 0}
            >
              {lifecycleOptions(form.kind).map(l => (
                <option key={l} value={l}>{LIFECYCLE_LABELS[l]}</option>
              ))}
            </Select>
            {form.leads > 0 && (
              <div className="md-span-full md-typescale-body-small text-muted">
                Usato da {form.leads} lead: tipo e ciclo di vita non si cambiano. Disattivalo e creane un altro.
              </div>
            )}
            {needsReason && (
              <Select
                className="md-span-full"
                id="ls-reason"
                label="Motivo di perdita *"
                value={form.lostReason}
                onChange={e => setForm(f => ({ ...f, lostReason: e.target.value }))}
                disabled={saving}
              >
                <option value="">Seleziona…</option>
                {(data?.lostReasons ?? []).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Select>
            )}
          </FormSection>

          {form.id && (
            <FormSection title="Opzioni" columns={1}>
              <Checkbox
                id="ls-active"
                label="Attivo (assegnabile ai lead)"
                checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                disabled={saving}
              />
            </FormSection>
          )}
        </>
      </Dialog>
    </Page>
  )
}
